import { DONATE_LINK } from '@core/utils/env';
import { css } from '@emotion/react';
import {
  FormProvider,
  FormSubmit,
  Input,
  Textarea,
  useFormStatus,
  validations,
} from '@web/components/form';
import * as React from 'react';
import { shareSwal } from '../layouts/share-modal';
import { theme } from '../theme';
import { FAQQuestions } from './FAQQuestions';
import { HowToQuestions } from './HowToQuestions';
import { TMApi } from '@web/services/tmapi';

export type SupportTabs = typeof SupportTabs[keyof typeof SupportTabs];
export const SupportTabs = {
  Root: 'root',
  FAQ: 'faq',
  icon: 'icon',
  howto: 'howto',
  video: 'video',
  contact: 'contact',
  share: 'share',
  // donate: 'donate',
} as const;

export type SupportDevices = typeof SupportDevices[keyof typeof SupportDevices];
export const SupportDevices = {
  ios: 'ios',
  android: 'android',
} as const;

export const SupportTabExtras: {
  [key in SupportTabs]: {
    title: string;
    Component?: React.ComponentType;
    onMount?: () => void;
  };
} = {
  [SupportTabs.Root]: {
    title: 'Time Machine Help',
  },
  [SupportTabs.FAQ]: {
    title: "Time Machine FAQ's",
    Component: function FAQ() {
      const [openAnswers, setOpenAnswers] = React.useState<
        Array<FAQQuestions[number]['id']>
      >([]);

      const toggleIsOpen = (id: FAQQuestions[number]['id']) => () => {
        if (openAnswers.includes(id)) {
          setOpenAnswers((prev) => prev.filter((a) => a !== id)); // remove from openAnswers
        } else {
          setOpenAnswers((prev) => [...prev, id]); // add to openAnswers
        }
      };

      const isOpen = (id: FAQQuestions[number]['id']) =>
        openAnswers.includes(id);

      return (
        <>
          {FAQQuestions.map(({ id, question, answer }) => (
            <div key={id} css={styles.accordion}>
              <h2 className="accordion-title">
                <button
                  type="button"
                  className={isOpen(id) ? 'shown' : 'hidden'}
                  onClick={toggleIsOpen(id)}
                >
                  {question}
                </button>
              </h2>

              <div
                id={`a${id}`}
                className="accordion-answer"
                css={isOpen(id) ? styles.shown : styles.hidden}
              >
                {answer}
              </div>
            </div>
          ))}
        </>
      );
    },
  },
  [SupportTabs.icon]: {
    title: 'Add Time Machine to your Home Screen',
    Component: function SupportIcon() {
      return (
        <>
          <section css={styles.section}>
            <h3 id="#ios" css={styles.sectionTitle}>
              iPhone
            </h3>

            <ul className="counter">
              <li>
                <p>Launch Safari on your iPhone or iPad.</p>
              </li>

              <li>
                <p>
                  Navigate to{' '}
                  <a href="https://app.timemachine.love">Time Machine</a>.
                </p>
              </li>

              <li>
                <p>Tap the Share icon.</p>
              </li>

              <li>
                <p>
                  Scroll down to the list of actions and tap 'Add to Home
                  Screen'. (If you don't see the action, scroll to the bottom
                  and tap 'Edit Actions', then tap 'App' next to the 'Add to
                  Home Screen' action. After that, you'll be able to select it
                  from the Share Sheet.)
                </p>

                <div css={styles.sectionScreenshots}>
                  <h4>Screenshots</h4>
                </div>
              </li>

              <li>
                <p>Tap 'Add' in the toip-right corner of the screen.</p>

                <div css={styles.sectionScreenshots}>
                  <h4>Screenshots</h4>
                </div>
              </li>
            </ul>

            <p css={styles.info}>
              The Time Machine will appear in the next available space on your
              device's 'Home Screen'.
            </p>
          </section>

          <section css={styles.section}>
            <h3 id="#android" css={styles.sectionTitle}>
              Android
            </h3>

            <ul className="counter">
              <li>
                <p>Launch Chrome on your Android device.</p>
              </li>

              <li>
                <p>
                  Navigate to{' '}
                  <a href="https://app.timemachine.love">Time Machine</a>.
                </p>
              </li>

              <li>
                <p>
                  Tap the 'Menu' icon in the upper right-hand corner of the
                  screen.
                </p>
              </li>

              <li>
                <p>
                  Scroll down to the list of actions and tap 'Add to Home
                  Screen'.
                </p>

                <div css={styles.sectionScreenshots}>
                  <h4>Screenshots</h4>
                </div>
              </li>
            </ul>

            <p css={styles.info}>
              The Time Machine will appear in the next available space on your
              device's 'Home Screen'
            </p>
          </section>
        </>
      );
    },
  },
  [SupportTabs.howto]: {
    title: 'How To Guides',
    Component: function HowTo() {
      const [openAnswers, setOpenAnswers] = React.useState<
        Array<FAQQuestions[number]['id']>
      >([]);

      const toggleIsOpen = (id: FAQQuestions[number]['id']) => () => {
        if (openAnswers.includes(id)) {
          setOpenAnswers((prev) => prev.filter((a) => a !== id)); // remove from openAnswers
        } else {
          setOpenAnswers((prev) => [...prev, id]); // add to openAnswers
        }
      };

      const isOpen = (id: FAQQuestions[number]['id']) =>
        openAnswers.includes(id);

      return (
        <>
          {HowToQuestions.map(({ id, question, answer }) => (
            <div key={id} css={styles.accordion}>
              <h2 className="accordion-title">
                <button
                  type="button"
                  className={isOpen(id) ? 'shown' : 'hidden'}
                  onClick={toggleIsOpen(id)}
                >
                  {question}
                </button>
              </h2>

              <div
                className="accordion-answer"
                css={isOpen(id) ? styles.shown : styles.hidden}
              >
                {answer}
              </div>
            </div>
          ))}
        </>
      );
    },
  },
  [SupportTabs.video]: {
    title: 'Intro Video',
    Component: function SupportVideo() {
      return (
        <iframe
          title="vimeo-player"
          src="https://player.vimeo.com/video/642511832?h=37e80fa09a"
          width="640"
          height="360"
          frameBorder="0"
          allowFullScreen
        />
      );
    },
  },
  [SupportTabs.contact]: {
    title: 'Contact Support',
    Component: function SupportContact() {
      type ContactFormData = { email: string; message: string };

      const [status, setStatus] = useFormStatus();

      const onFormSubmit: FormSubmit<ContactFormData> = async (formData) => {
        setStatus({ type: 'info', el: 'sending message...' });
        try {
          let tmapi = new TMApi();
          await tmapi.sendSupportMessage(formData.message, formData.email);
          setStatus({ type: 'success', el: 'message sent!' });
        } catch (_error) {
          const error =
            _error instanceof Error
              ? _error
              : new Error(JSON.stringify(_error));

          setStatus({ type: 'error', el: error.message });
        }
      };

      return (
        <FormProvider<ContactFormData>
          defaultValues={{ email: '', message: '' }}
          onSubmit={onFormSubmit}
          submitButtonText="Send Message"
          status={status}
        >
          <p>
            Thank you for reaching out. Your reports help make time machine
            better.
          </p>
          <Input
            name="email"
            type="email"
            label="Email"
            placeholder="Enter your email"
            autoFocus
            config={{
              required: validations.required,
              pattern: validations.email,
            }}
          />

          <Textarea
            name="message"
            label="Enter a message"
            placeholder="I would like..."
            rows={12}
            config={{
              required: validations.required,
              minLength: validations.minLength,
              maxLength: {
                value: validations.maxLength.value * 25,
                message: `Too long (${validations.maxLength.value * 25})`,
              },
            }}
          />
        </FormProvider>
      );
    },
  },
  [SupportTabs.share]: {
    title: 'Share Time Machine',
    onMount: shareSwal,
  },
  // [SupportTabs.donate]: {
  //   title: 'Donate to Time Machine',
  //   onMount: () => {
  //     window.location.href = DONATE_LINK;
  //   },
  // },
};

const styles = {
  accordion: css`
    padding: ${theme.space[4]} ${theme.space[4]} ${theme.space[4]};

    &:not(:last-child) {
      border-bottom: ${theme.borders['2px']};
      border-color: ${theme.colors.info};
    }
    .accordion-title {
      button {
        background: none;
        border: none;
        display: block;
        padding: 0;
        margin: 0;
        font-size: inherit;
        text-align: left;
        font-weight: inherit;
        color: inherit;
        cursor: pointer;
        padding: 2%;

        &:hover,
        &:focus,
        &.shown {
          text-decoration: underline;
          background: ${theme.colors.whiteAlpha[200]};
          border-radius: ${theme.radii.md};
        }
      }
    }

    .accordion-answer {
    }
  `,

  shown: css`
    visibility: visible;
    font-weight: ${theme.fontWeights.normal};
    line-height: ${theme.lineHeights.tall};
    margin-top: ${theme.space[4]};
    border-bottom: 1px;
    border-color: ${theme.colors.info};
    font-size: 1.2em;
  `,

  hidden: css`
    visibility: hidden;
    height: 0;
    width: 0;
  `,

  section: css`
    margin: ${theme.space[4]} 0;
    padding: ${theme.space[4]};
    background-color: ${theme.colors.whiteAlpha[300]};
    border-radius: ${theme.radii.md};

    .counter {
      counter-reset: section;

      li {
        counter-increment: section;
        position: relative;
        margin: ${theme.space[4]} 0;
        display: grid;
        grid-template-areas:
          'icon text'
          'screenshots screenshots';
        align-items: center;
        justify-content: start;

        p {
          grid-area: text;
          line-height: ${theme.lineHeights.tall};
        }

        &:before {
          grid-area: icon;
          align-self: start;
          content: counter(section);
          text-align: center;
          padding-top: 7%;
          height: ${theme.space[6]};
          width: ${theme.space[6]};
          background-color: ${theme.colors.whiteAlpha[900]};
          color: ${theme.colors.purple[800]};
          font-weight: ${theme.fontWeights.semibold};
          border-radius: ${theme.radii.md};
          margin-right: ${theme.space[4]};
          border-radius: ${theme.radii.full};
        }
      }
    }
  `,

  sectionTitle: css`
    margin: ${theme.space[4]} 0;
    font-size: ${theme.fontSizes['3xl']};
    font-weight: ${theme.fontWeights.bold};
  `,

  sectionScreenshots: css`
    grid-area: screenshots;
    margin: ${theme.space[2]} 0;
  `,

  info: css`
    text-align: center;
    margin: ${theme.space[4]} auto;
    font-weight: ${theme.fontWeights.semibold};
  `,
};
